import { useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import {
  Button,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  useTheme,
  FormControl,
  Box
} from '@mui/material';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { IObject, IObjectInput } from 'src/models/objectManagement';
import CloseIcon from '@mui/icons-material/Close';
import {
  reducerRemoveTempObjectInput,
  reducerUpdateAllTempObjectInput,
  reducerUpdateTempObjectInput
} from 'src/features/objectManagement';
import InputSocialProfile from '../ModalManageObjectRetail/components/InputSocialProfile';
import InputCardSocialProfile from '../ModalManageObjectRetail/components/InputCardSocialProfile';
import useOnboarding from 'src/services/useOnboarding';
import useObjectManagement from 'src/services/useObjectManagement';
import useUsers from 'src/services/useUsers';
import {
  GET_OBJECTS_WITH_ENTITY,
  GET_OBJECTS_WITH_STATUS
} from 'src/services/graphql/objectManagement/query';
import { useApolloClient } from '@apollo/client';
import Confirmation from 'src/components/Confirmation';
import { getSocmedDomain } from 'src/utils/getSocmedDomain';
import NotAllowedTooltip from 'src/components/NotAllowedTooltip';
import DataCollectionInformation from 'src/components/DataCollectionInformation';
import { t } from 'i18next';

export interface ISocialProfileInput {
  socialProfiles: Array<IObjectInput>;
}

const ModalAddSocialProfileRetail = ({
  buttonVariant,
  showButton,
  handleCloseModal,
  onDone
}: {
  buttonVariant: 'outlined' | 'contained';
  showButton: boolean;
  handleCloseModal: () => void;
  onDone?: () => void;
}) => {
  const [open, setOpen] = useState(showButton ? false : true);

  const dispatch = useAppDispatch();

  const handleClickOpen = () => {
    setOpen(true);
    dispatch(reducerUpdateAllTempObjectInput([]));
  };

  const handleClose = () => {
    handleCloseModal();
    setOpen(false);
    dispatch(reducerUpdateAllTempObjectInput([]));
  };

  const theme = useTheme();

  const { tempInputObjects, objectList } = useAppSelector(
    (state) => state.objectManagement
  );

  const {
    id: projectId,
    onBoarding: { basicObjectQuota, basicObjectQuotaUsed },
    role: userRole
  } = useAppSelector((state) => state.users.project);

  const { createObjects, responseCreateObjects } = useOnboarding();
  const { getObjectList } = useObjectManagement();
  const { getProjectDetailResult } = useUsers();
  const client = useApolloClient();
  const [openAddConfirmation, setOpenAddConfirmation] =
    useState<boolean>(false);

  const objectListSocialProfile = useMemo((): Array<IObject> => {
    return objectList.data.filter((object) => object.streamType === 'account');
  }, [objectList]);

  const isObjectAlreadyIncluded = () => {
    return objectListSocialProfile.some((object) =>
      tempInputObjects
        .map((item) => item.objectName)
        .includes(getSocmedDomain(object.socialMedia) + object.objectName)
    );
  };

  const handleSubmit = () => {
    const socialProfilePayload = {
      projectId: projectId,
      streamType: 'account',
      objectInput: [
        ...tempInputObjects.map(
          ({ streamType, ...socialProfile }) => socialProfile
        )
      ]
    };
    createObjects({
      variables: socialProfilePayload
    }).then(() => {
      getObjectList(projectId);
      handleClose();
      getProjectDetailResult(projectId);
      client.refetchQueries({
        include: [GET_OBJECTS_WITH_ENTITY, GET_OBJECTS_WITH_STATUS]
      });
      if (onDone) {
        onDone();
      }
    });
  };

  const handleAddObject = (inputObject: IObjectInput) => {
    dispatch(reducerUpdateTempObjectInput(inputObject));
  };

  const handleRemove = (id: string) => {
    dispatch(reducerRemoveTempObjectInput(id));
  };

  const isMember = userRole === 'member';

  return (
    <>
      {showButton ? (
        isMember ? (
          <NotAllowedTooltip>
            <Button
              variant={buttonVariant}
              sx={
                buttonVariant === 'outlined'
                  ? {
                      color: theme.palette.primary.main,
                      border: `1px solid ${theme.palette.primary.main}`
                    }
                  : {
                      backgroundColor: theme.palette.primary.dark
                    }
              }
            >
              + {t('add social profile')}
            </Button>
          </NotAllowedTooltip>
        ) : (
          <Button
            variant={buttonVariant}
            onClick={handleClickOpen}
            sx={
              buttonVariant === 'outlined'
                ? {
                    color: theme.palette.primary.main,
                    border: `1px solid ${theme.palette.primary.main}`
                  }
                : {
                    backgroundColor: theme.palette.primary.dark
                  }
            }
          >
            + {t('add social profile')}
          </Button>
        )
      ) : null}
      <Dialog onClose={handleClose} open={open} fullWidth maxWidth={'md'}>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle
          sx={{
            m: theme.spacing(3, 3, 0),
            p: theme.spacing(2, 3, 0, 3)
          }}
        >
          <Typography variant="h4">{t('add social profile')}</Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            m: { xs: 0, sm: theme.spacing(1, 3) }
          }}
        >
          <DataCollectionInformation />
          <Grid container spacing={2} sx={{ my: 1 }}>
            <Grid item lg={6}>
              <InputSocialProfile onAdd={handleAddObject} />
            </Grid>
            <Grid item lg={6}>
              <FormControl fullWidth size="medium">
                <InputCardSocialProfile
                  title={`${t('added social media')} (${
                    basicObjectQuotaUsed + tempInputObjects.length
                  } / ${basicObjectQuota})`}
                  information={t('tracked social media will appear here')}
                  objectList={tempInputObjects}
                  onRemove={handleRemove}
                />
              </FormControl>
            </Grid>
            <Grid item lg={12}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 1
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ width: theme.spacing(15), height: theme.spacing(5) }}
                  disabled={
                    responseCreateObjects.loading ||
                    tempInputObjects.length === 0
                  }
                  onClick={() =>
                    isObjectAlreadyIncluded()
                      ? setOpenAddConfirmation(true)
                      : handleSubmit()
                  }
                >
                  {t('done')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      <Confirmation
        title={'Add this object?'}
        labelButton={'Add'}
        message={`The social profile you want to add has already been added.`}
        open={openAddConfirmation}
        onClose={() => setOpenAddConfirmation(false)}
        onOk={() => {
          handleSubmit();
        }}
      />
    </>
  );
};

export default ModalAddSocialProfileRetail;
